import { Text } from '@doordash/design-language'
import { isFeatureFlagEnabled } from '@doordash/lib-feature-flags'
import * as React from 'react'
import { useEffect } from 'react'
import { connect } from 'react-redux'
import { detectWebviewOs } from '../../../utilities/userAgent'
import { loadUrl } from '../../../webview'
import { OsTypes } from '../../../webview/types'
import { i18nKeys } from 'translationTypes'
import { toggleAlreadyStartedSigningUpModal } from '../../../actions'
import {
  ANDROID_CLOSE_DEEPLINK,
  COUNTRY_SHORTCODES,
  IOS_CLOSE_DEEPLINK,
} from '../../../constants'
import {
  GERMANY_DX_ONBOARDING_V2,
  DX_CRO_LP_GRADUATION_Q3_2020,
} from '../../../experimentConstants'
import i18n from '../../../i18n'
import {
  AlreadyStartedEnum,
  AlreadyStartedSigningUpModalProps,
  SupportedCountryCode,
} from '../../../models/types'
import DetailSignupPage from '../detail-signup'
import SimpleSignUpPage from '../simple-sign-up'
import { Mode } from '../simple-sign-up/SimpleSignUpPage'
import { countryCodeToPrivacyPolicyUrl, countryCodeToTosUrl } from './legalUrls'

declare global {
  interface Window {
    view: {
      pendingExternalRedirect?: string
    }
  }
}

/**
 * Some countries should show a simple sign up page that
 * doesn't contain particular business messaging (eg. Dashers being
 * able to choose exactly when they can dash)
 */
const simpleSignUpPageCountryCodes: SupportedCountryCode[] = [
  COUNTRY_SHORTCODES.DE,
]

/**
 * Some countries have disclaimers that need to be rendered
 */
const simpleSignUpPageCountryCodeToDisclaimer: Partial<Record<
  SupportedCountryCode,
  JSX.Element
>> = {
  DE: (
    <Text styles={Text.Styles.Body2}>
      {i18n.t(i18nKeys.signupForm__employmentDisclaimerDE)}
    </Text>
  ),
}

type SignUpProps = {
  signupCountryCode?: SupportedCountryCode
  toggleAlreadyStartedSigningUpModal: (
    data: AlreadyStartedSigningUpModalProps
  ) => Promise<void>
  alreadyStartedSigningUpModal: AlreadyStartedSigningUpModalProps
}

/**
 * SignUpPage
 *
 * Controls which signup page should be rendered.
 * For some geos, the detailed sign up page is unsuitable because
 * the additional information does not apply.
 */
const SignUpPage: React.FC<SignUpProps> = ({
  signupCountryCode,
  toggleAlreadyStartedSigningUpModal,
  alreadyStartedSigningUpModal,
}) => {
  useEffect(() => {
    // The BFF can set this flag to instruct opening an external URL.
    // Currently this is only triggered from the signup page.
    const { pendingExternalRedirect } = window.view
    if (pendingExternalRedirect) {
      loadUrl(pendingExternalRedirect)
      const os = detectWebviewOs()
      let newLocation: string
      switch (os) {
        case OsTypes.iOS:
          newLocation = IOS_CLOSE_DEEPLINK
          break
        case OsTypes.Android:
          newLocation = ANDROID_CLOSE_DEEPLINK
          break
        default:
          newLocation = pendingExternalRedirect
          break
      }

      window.location.assign(newLocation)
    }
  }, [])

  const handleExistingUserRequest = (
    options?: AlreadyStartedSigningUpModalProps
  ) => {
    toggleAlreadyStartedSigningUpModal({
      type: AlreadyStartedEnum.Default,
      ...options,
      isShowing: true,
    })
  }

  const handleModalClose = () => {
    toggleAlreadyStartedSigningUpModal({
      isShowing: false,
    })
  }

  const {
    isShowing: isAlreadyStartedSigningUpModalOpen,
  } = alreadyStartedSigningUpModal

  if (
    simpleSignUpPageCountryCodes.includes(signupCountryCode) &&
    !isFeatureFlagEnabled(DX_CRO_LP_GRADUATION_Q3_2020)
  ) {
    const mode: Mode = isFeatureFlagEnabled(GERMANY_DX_ONBOARDING_V2)
      ? 'v2'
      : 'default'

    return (
      <SimpleSignUpPage
        disclaimer={simpleSignUpPageCountryCodeToDisclaimer[signupCountryCode]}
        termsOfAgreementUrl={countryCodeToTosUrl[signupCountryCode]}
        privacyPolicyUrl={countryCodeToPrivacyPolicyUrl[signupCountryCode]}
        preselectedCountryCode={signupCountryCode}
        applicantCountryCode={signupCountryCode}
        onExistingUserRequest={handleExistingUserRequest}
        onExistingUserModalCloseRequest={handleModalClose}
        isExistingUserModalOpen={isAlreadyStartedSigningUpModalOpen}
        mode={mode}
      />
    )
  }
  /**
   * IMPROVE @peterj35-dd: Tease apart the submitDasherApplication handler
   * from the DetailSignupPage to indicate at a higher level
   * that ultimately the Simple and Detail SignupPages leverage
   * the same APIs
   */
  return <DetailSignupPage />
}

const mapStateToProps = state => ({
  alreadyStartedSigningUpModal: state.alreadyStartedSigningUpModal,
  signupCountryCode: state.signupCountryShortname,
})

const mapDispatchToProps = {
  toggleAlreadyStartedSigningUpModal,
}

export default connect(mapStateToProps, mapDispatchToProps)(SignUpPage)
